import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  MdSupportAgent,
  MdSearch,
  MdRefresh,
  MdFileDownload,
  MdVisibility,
  MdClose,
  MdContentCopy,
  MdCheck,
  MdCalendarToday,
  MdLocationOn,
  MdEmail,
  MdPhone,
  MdFingerprint,
  MdClear,
  MdDeleteOutline,
  MdChat,
  MdQuestionAnswer,
  MdPerson,
} from "react-icons/md";
import { apiFetch, API_URL } from "../../services/api";
import { getCurrentUserRole, hasPermission } from "../../utils/rbac";
import ShowSnacker from "./ShowSnacker";
import "../../styles/DashBoard_CustomerCare.css";

export interface CustomerCareRecord {
  macId: string;
  emailId: string;
  mobileNumber: string;
  name: string;
  city: string;
  state: string;
  country: string;
  zipcode: string;
  message: string[] | string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerCareStats {
  totalRecords: number;
  totalMessages: number;
  todayCount: number;
  monthCount: number;
}

export default function DashBoardCustomerCare() {
  const navigate = useNavigate();

  // Data & State
  const [records, setRecords] = useState<CustomerCareRecord[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [serverError, setServerError] = useState<string | null>(null);

  // Filters State
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [timeFilter, setTimeFilter] = useState<"ALL" | "TODAY" | "MONTH">("ALL");

  // Pagination State
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 10;

  // Modal State
  const [selectedRecord, setSelectedRecord] = useState<CustomerCareRecord | null>(null);
  const [recordToDelete, setRecordToDelete] = useState<CustomerCareRecord | null>(null);
  const [copiedMac, setCopiedMac] = useState<string | null>(null);
  const [snackMessage, setSnackMessage] = useState<string>("");
  const [snackType, setSnackType] = useState<"success" | "error" | "info">("info");
  const [snackOpen, setSnackOpen] = useState<boolean>(false);

  // Role permissions
  const [userRole, setUserRole] = useState(getCurrentUserRole());
  const canDelete = hasPermission("canDeleteCustomerCare", userRole);

  const showNotification = (msg: string, type: "success" | "error" | "info" = "info") => {
    setSnackMessage(msg);
    setSnackType(type);
    setSnackOpen(true);
  };

  // Format colon-separated MAC address
  const formatMacDisplay = (raw?: string | null): string => {
    if (!raw) return "N/A";
    const cleaned = raw.trim().toUpperCase().replace(/[^0-9A-F]/g, "");
    if (cleaned.length === 12) {
      return cleaned.match(/.{1,2}/g)?.join(":") || raw.toUpperCase();
    }
    return raw.toUpperCase();
  };

  // Helper to extract message array from record safely
  const getMessagesList = (record: CustomerCareRecord): string[] => {
    if (!record || !record.message) return [];
    if (Array.isArray(record.message)) {
      return record.message.map((m) => (typeof m === "object" ? JSON.stringify(m) : String(m)));
    }
    if (typeof record.message === "string") {
      try {
        const parsed = JSON.parse(record.message);
        if (Array.isArray(parsed)) {
          return parsed.map((m) => (typeof m === "object" ? JSON.stringify(m) : String(m)));
        }
      } catch (_) {}
      return [record.message];
    }
    return [String(record.message)];
  };

  // Fetch all customer care queries from backend
  const fetchCustomerCareData = useCallback(async () => {
    setLoading(true);
    setServerError(null);
    try {
      let res = await apiFetch("/customer-care/all", { method: "GET" });
      if (res.status === 404) {
        res = await apiFetch("/api/customer-care/all", { method: "GET" });
      }
      if (res.status === 404) {
        res = await apiFetch("/customer-care", { method: "GET" });
      }
      if (res.status === 404) {
        res = await apiFetch("/api/customer-care", { method: "GET" });
      }
      if (!res.ok) {
        if (res.status === 403) {
          throw new Error("Access Denied: You do not have permission to view customer queries.");
        }
        throw new Error(`Failed to load customer care inquiries (HTTP ${res.status}) [URL: ${API_URL}]`);
      }
      const data = await res.json();
      const loaded: CustomerCareRecord[] =
        data.customerCareRecords || data.CustomerCareRecords || data.records || (Array.isArray(data) ? data : []);
      setRecords(loaded);
    } catch (err: any) {
      console.error("fetchCustomerCareData error:", err);
      setServerError(err.message || "Error loading customer queries");
      showNotification(err.message || "Error fetching records", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomerCareData();
  }, [fetchCustomerCareData]);

  // Compute stats
  const stats: CustomerCareStats = useMemo(() => {
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const yearMonthStr = now.toISOString().slice(0, 7);

    let totalMsgCount = 0;
    let today = 0;
    let month = 0;

    records.forEach((r) => {
      const msgs = getMessagesList(r);
      totalMsgCount += msgs.length;

      if (r.createdAt) {
        const recDate = r.createdAt.slice(0, 10);
        if (recDate === todayStr) today++;
        if (r.createdAt.startsWith(yearMonthStr)) month++;
      }
    });

    return {
      totalRecords: records.length,
      totalMessages: totalMsgCount,
      todayCount: today,
      monthCount: month,
    };
  }, [records]);

  // Filtering
  const filteredRecords = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const now = new Date();
    const todayStr = now.toISOString().slice(0, 10);
    const yearMonthStr = now.toISOString().slice(0, 7);

    return records.filter((rec) => {
      // 1. Time filter
      if (timeFilter === "TODAY") {
        if (!rec.createdAt || !rec.createdAt.startsWith(todayStr)) return false;
      } else if (timeFilter === "MONTH") {
        if (!rec.createdAt || !rec.createdAt.startsWith(yearMonthStr)) return false;
      }

      // 2. Search query filter
      if (!q) return true;

      const macClean = (rec.macId || "").toLowerCase().replace(/[^0-9a-f]/g, "");
      const searchClean = q.replace(/[^0-9a-f]/g, "");
      const macMatches = searchClean.length >= 3 && macClean.includes(searchClean);

      const nameMatches = (rec.name || "").toLowerCase().includes(q);
      const emailMatches = (rec.emailId || "").toLowerCase().includes(q);
      const phoneMatches = (rec.mobileNumber || "").toLowerCase().includes(q);
      const cityMatches = (rec.city || "").toLowerCase().includes(q);
      const stateMatches = (rec.state || "").toLowerCase().includes(q);
      const countryMatches = (rec.country || "").toLowerCase().includes(q);
      const zipMatches = (rec.zipcode || "").toLowerCase().includes(q);

      const msgs = getMessagesList(rec);
      const messageMatches = msgs.some((m) => m.toLowerCase().includes(q));

      return (
        macMatches ||
        nameMatches ||
        emailMatches ||
        phoneMatches ||
        cityMatches ||
        stateMatches ||
        countryMatches ||
        zipMatches ||
        messageMatches
      );
    });
  }, [records, searchQuery, timeFilter]);

  // Pagination calculation
  const totalPages = Math.ceil(filteredRecords.length / itemsPerPage) || 1;
  const paginatedRecords = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredRecords.slice(start, start + itemsPerPage);
  }, [filteredRecords, currentPage]);

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Copy MAC ID to clipboard
  const handleCopyMac = (mac: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(mac);
    setCopiedMac(mac);
    showNotification(`MAC ID copied: ${mac}`, "success");
    setTimeout(() => setCopiedMac(null), 2500);
  };

  // Export to Excel (.xlsx)
  const handleExportExcel = () => {
    if (filteredRecords.length === 0) {
      showNotification("No records available to export", "info");
      return;
    }

    const exportRows = filteredRecords.map((r, index) => {
      const msgs = getMessagesList(r);
      return {
        "S.No": index + 1,
        "Customer Name": r.name || "N/A",
        "Email ID": r.emailId || "N/A",
        "Mobile Number": r.mobileNumber || "N/A",
        "MAC ID": formatMacDisplay(r.macId),
        City: r.city || "N/A",
        State: r.state || "N/A",
        Country: r.country || "N/A",
        Zipcode: r.zipcode || "N/A",
        "Total Messages": msgs.length,
        "Latest Message": msgs[msgs.length - 1] || "N/A",
        "All Messages": msgs.join(" | "),
        "Created At": r.createdAt ? new Date(r.createdAt).toLocaleString() : "N/A",
        "Last Updated": r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "N/A",
      };
    });

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customer_Queries");

    const dateStr = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(workbook, `Studds_Customer_Care_Queries_${dateStr}.xlsx`);
    showNotification(`Exported ${exportRows.length} customer query records to Excel`, "success");
  };

  // Delete Customer Care Record (Super Admin only)
  const handleDeleteRecord = async () => {
    if (!recordToDelete) return;
    try {
      const res = await apiFetch(`/customer-care/delete/${encodeURIComponent(recordToDelete.macId)}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        throw new Error("Failed to delete customer care record");
      }
      showNotification(`Customer care record for MAC ${recordToDelete.macId} deleted successfully`, "success");
      setRecords((prev) => prev.filter((r) => r.macId !== recordToDelete.macId));
      if (selectedRecord && selectedRecord.macId === recordToDelete.macId) {
        setSelectedRecord(null);
      }
      setRecordToDelete(null);
    } catch (err: any) {
      showNotification(err.message || "Error deleting record", "error");
    }
  };

  return (
    <div className="cc-container">
      {/* Header Row */}
      <div className="cc-header-row">
        <div className="cc-title-section">
          <h2>
            <MdSupportAgent className="title-icon" />
            Customer Care Queries
          </h2>
          <p>
            View and manage customer support inquiries, feedback, and query threads submitted via mobile application.
          </p>
        </div>

        <div className="cc-actions-row">
          <button className="cc-btn-export" onClick={handleExportExcel} title="Download queries as Excel sheet">
            <MdFileDownload size={18} />
            Export to Excel
          </button>

          <div className="cc-round-icons">
            <button
              className={`cc-round-btn ${loading ? "spinning" : ""}`}
              onClick={fetchCustomerCareData}
              title="Refresh Customer Queries"
            >
              <MdRefresh size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="cc-stats-grid">
        <div className="cc-stat-card">
          <div className="cc-stat-icon-wrapper total">
            <MdSupportAgent />
          </div>
          <div className="cc-stat-info">
            <span className="cc-stat-value">{stats.totalRecords.toLocaleString()}</span>
            <span className="cc-stat-label">Total Inquiries</span>
          </div>
        </div>

        <div className="cc-stat-card">
          <div className="cc-stat-icon-wrapper messages">
            <MdChat />
          </div>
          <div className="cc-stat-info">
            <span className="cc-stat-value">{stats.totalMessages.toLocaleString()}</span>
            <span className="cc-stat-label">Total Messages</span>
          </div>
        </div>

        <div className="cc-stat-card">
          <div className="cc-stat-icon-wrapper today">
            <MdCalendarToday />
          </div>
          <div className="cc-stat-info">
            <span className="cc-stat-value">{stats.todayCount.toLocaleString()}</span>
            <span className="cc-stat-label">Inquiries Today</span>
          </div>
        </div>

        <div className="cc-stat-card">
          <div className="cc-stat-icon-wrapper month">
            <MdQuestionAnswer />
          </div>
          <div className="cc-stat-info">
            <span className="cc-stat-value">{stats.monthCount.toLocaleString()}</span>
            <span className="cc-stat-label">This Month</span>
          </div>
        </div>
      </div>

      {/* Filter and Search Card */}
      <div className="cc-filter-card">
        <div className="cc-filter-top-row">
          <div className="cc-search-box">
            <MdSearch className="cc-search-icon" />
            <input
              type="text"
              className="cc-search-input"
              placeholder="Search by Customer Name, Email, Phone, MAC ID, City, or Query..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
            />
            {searchQuery && (
              <button
                className="cc-search-clear-btn"
                onClick={() => setSearchQuery("")}
                title="Clear Search"
              >
                <MdClear size={16} />
              </button>
            )}
          </div>

          <div className="cc-time-pills">
            <button
              className={`cc-pill-btn ${timeFilter === "ALL" ? "active" : ""}`}
              onClick={() => {
                setTimeFilter("ALL");
                setCurrentPage(1);
              }}
            >
              All Time
            </button>
            <button
              className={`cc-pill-btn ${timeFilter === "TODAY" ? "active" : ""}`}
              onClick={() => {
                setTimeFilter("TODAY");
                setCurrentPage(1);
              }}
            >
              Today
            </button>
            <button
              className={`cc-pill-btn ${timeFilter === "MONTH" ? "active" : ""}`}
              onClick={() => {
                setTimeFilter("MONTH");
                setCurrentPage(1);
              }}
            >
              This Month
            </button>
          </div>
        </div>

        <div className="cc-active-count-text">
          Showing <strong>{filteredRecords.length}</strong> of <strong>{records.length}</strong> total inquiries
        </div>
      </div>

      {/* Main Data Table */}
      <div className="cc-table-card">
        {loading ? (
          <div className="cc-loading-state">
            <div className="cc-spinner"></div>
            <span>Loading customer queries...</span>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="cc-empty-state">
            <MdSupportAgent className="cc-empty-icon" />
            <h3>No Customer Queries Found</h3>
            <p>
              {searchQuery || timeFilter !== "ALL"
                ? "No customer support requests match your current search or date filter."
                : "No customer queries have been submitted to customer care yet."}
            </p>
            {(searchQuery || timeFilter !== "ALL") && (
              <button
                className="cc-btn-reset-filters"
                onClick={() => {
                  setSearchQuery("");
                  setTimeFilter("ALL");
                }}
              >
                Reset All Filters
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="cc-table-responsive">
              <table className="cc-data-table">
                <thead>
                  <tr>
                    <th>Customer</th>
                    <th>Contact Details</th>
                    <th>Device MAC ID</th>
                    <th>Location</th>
                    <th>Queries</th>
                    <th>Latest Message</th>
                    <th>Date</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedRecords.map((record) => {
                    const messages = getMessagesList(record);
                    const latestMessage = messages.length > 0 ? messages[messages.length - 1] : "No message";
                    const initial = record.name ? record.name.charAt(0).toUpperCase() : "C";

                    return (
                      <tr key={record.macId}>
                        <td>
                          <div className="cc-customer-cell">
                            <div className="cc-avatar-circle">{initial}</div>
                            <div className="cc-customer-info">
                              <span className="cc-customer-name">{record.name || "Unknown Customer"}</span>
                              <span className="cc-customer-subtext">MAC: {record.macId}</span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
                            <span style={{ fontSize: "13px", color: "#1e293b", fontWeight: 500 }}>
                              {record.mobileNumber || "N/A"}
                            </span>
                            <span style={{ fontSize: "12px", color: "#64748b" }}>
                              {record.emailId || "N/A"}
                            </span>
                          </div>
                        </td>

                        <td>
                          <div className="cc-mac-badge">
                            <span>{formatMacDisplay(record.macId)}</span>
                            <button
                              className="cc-copy-btn"
                              title="Copy MAC ID"
                              onClick={(e) => handleCopyMac(record.macId, e)}
                            >
                              {copiedMac === record.macId ? (
                                <MdCheck size={14} color="#10b981" />
                              ) : (
                                <MdContentCopy size={13} />
                              )}
                            </button>
                          </div>
                        </td>

                        <td>
                          <div style={{ display: "flex", flexDirection: "column", gap: "2px", fontSize: "12.5px" }}>
                            <span>
                              {[record.city, record.state].filter(Boolean).join(", ") || "N/A"}
                            </span>
                            <span style={{ color: "#94a3b8", fontSize: "11.5px" }}>
                              {[record.country, record.zipcode].filter(Boolean).join(" - ")}
                            </span>
                          </div>
                        </td>

                        <td>
                          <span className="cc-msg-count-badge">
                            <MdChat size={13} />
                            {messages.length} {messages.length === 1 ? "query" : "queries"}
                          </span>
                        </td>

                        <td>
                          <div className="cc-snippet-preview" title={latestMessage}>
                            {latestMessage}
                          </div>
                        </td>

                        <td>
                          <span style={{ fontSize: "12.5px", color: "#64748b", whiteSpace: "nowrap" }}>
                            {record.createdAt ? new Date(record.createdAt).toLocaleDateString() : "N/A"}
                          </span>
                        </td>

                        <td>
                          <div className="cc-actions-cell" style={{ justifyContent: "center" }}>
                            <button
                              className="cc-btn-view-thread"
                              onClick={() => setSelectedRecord(record)}
                              title="View Full Query Conversation"
                            >
                              <MdVisibility size={15} />
                              View Thread
                            </button>

                            {canDelete && (
                              <button
                                className="cc-btn-delete-row"
                                onClick={() => setRecordToDelete(record)}
                                title="Delete Customer Support Entry"
                              >
                                <MdDeleteOutline size={17} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="cc-pagination-bar">
                <span className="cc-pagination-text">
                  Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
                </span>

                <div className="cc-pagination-buttons">
                  <button
                    className="cc-page-btn"
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>

                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                    // Show pages around current
                    if (
                      page === 1 ||
                      page === totalPages ||
                      (page >= currentPage - 1 && page <= currentPage + 1)
                    ) {
                      return (
                        <button
                          key={page}
                          className={`cc-page-btn ${currentPage === page ? "active" : ""}`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      );
                    }
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return (
                        <span key={page} style={{ padding: "0 4px", color: "#94a3b8" }}>
                          ...
                        </span>
                      );
                    }
                    return null;
                  })}

                  <button
                    className="cc-page-btn"
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Query Thread Modal */}
      {selectedRecord && (
        <div className="cc-modal-overlay" onClick={() => setSelectedRecord(null)}>
          <div className="cc-modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="cc-modal-header">
              <div className="cc-modal-header-info">
                <h3>
                  <MdSupportAgent size={22} color="#2563eb" />
                  {selectedRecord.name || "Customer Inquiries"}
                </h3>
                <p>Support ticket conversation history for helmet device</p>
              </div>
              <button
                className="cc-modal-close-btn"
                onClick={() => setSelectedRecord(null)}
                title="Close"
              >
                <MdClose />
              </button>
            </div>

            <div className="cc-modal-body">
              {/* Customer Profile Grid */}
              <div className="cc-customer-meta-grid">
                <div className="cc-meta-item">
                  <span className="cc-meta-label">Customer Name</span>
                  <span className="cc-meta-value">
                    <MdPerson size={15} color="#64748b" />
                    {selectedRecord.name || "N/A"}
                  </span>
                </div>

                <div className="cc-meta-item">
                  <span className="cc-meta-label">Mobile Number</span>
                  <span className="cc-meta-value">
                    <MdPhone size={15} color="#64748b" />
                    {selectedRecord.mobileNumber || "N/A"}
                  </span>
                </div>

                <div className="cc-meta-item">
                  <span className="cc-meta-label">Email ID</span>
                  <span className="cc-meta-value">
                    <MdEmail size={15} color="#64748b" />
                    {selectedRecord.emailId || "N/A"}
                  </span>
                </div>

                <div className="cc-meta-item">
                  <span className="cc-meta-label">MAC ID</span>
                  <span className="cc-meta-value">
                    <MdFingerprint size={15} color="#64748b" />
                    {formatMacDisplay(selectedRecord.macId)}
                    <button
                      className="cc-copy-btn"
                      onClick={(e) => handleCopyMac(selectedRecord.macId, e)}
                      title="Copy MAC"
                    >
                      <MdContentCopy size={13} />
                    </button>
                  </span>
                </div>

                <div className="cc-meta-item" style={{ gridColumn: "1 / -1" }}>
                  <span className="cc-meta-label">Location</span>
                  <span className="cc-meta-value">
                    <MdLocationOn size={15} color="#64748b" />
                    {[
                      selectedRecord.city,
                      selectedRecord.state,
                      selectedRecord.country,
                      selectedRecord.zipcode,
                    ]
                      .filter(Boolean)
                      .join(", ") || "N/A"}
                  </span>
                </div>
              </div>

              {/* Messages Thread */}
              <div>
                <div className="cc-thread-title-row">
                  <h4>
                    <MdChat color="#2563eb" />
                    Submitted Queries ({getMessagesList(selectedRecord).length})
                  </h4>
                  <span style={{ fontSize: "12px", color: "#64748b" }}>
                    Registered: {selectedRecord.createdAt ? new Date(selectedRecord.createdAt).toLocaleString() : "N/A"}
                  </span>
                </div>

                <div className="cc-messages-list">
                  {getMessagesList(selectedRecord).map((msg, idx) => (
                    <div key={idx} className="cc-message-card">
                      <div className="cc-message-top">
                        <span className="cc-msg-pill">Query #{idx + 1}</span>
                        <button
                          className="cc-copy-btn"
                          title="Copy Query Text"
                          onClick={() => {
                            navigator.clipboard.writeText(msg);
                            showNotification("Query text copied to clipboard", "success");
                          }}
                        >
                          <MdContentCopy size={14} />
                        </button>
                      </div>
                      <div className="cc-msg-text">{msg}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="cc-modal-footer">
              <button
                className="cc-btn-modal-close"
                onClick={() => setSelectedRecord(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {recordToDelete && (
        <div className="cc-modal-overlay" onClick={() => setRecordToDelete(null)}>
          <div className="cc-modal-dialog cc-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="cc-delete-icon-wrapper">
              <MdDeleteOutline />
            </div>
            <h3>Delete Customer Query?</h3>
            <p>
              Are you sure you want to permanently delete customer care query records for MAC{" "}
              <strong>{recordToDelete.macId}</strong> ({recordToDelete.name || "Unknown"})? This action cannot be undone.
            </p>
            <div className="cc-delete-actions">
              <button className="cc-btn-cancel-del" onClick={() => setRecordToDelete(null)}>
                Cancel
              </button>
              <button className="cc-btn-confirm-del" onClick={handleDeleteRecord}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Snackbar feedback */}
      <ShowSnacker
        open={snackOpen}
        message={snackMessage}
        type={snackType}
        onClose={() => setSnackOpen(false)}
      />
    </div>
  );
}

